import { PayloadAction, createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    addNewInstaller,
    getAllInstallers,
    removeInstallers,
    updateInstaller,
} from '../../core/api/endpoints/versions/versionsApi';
import { AddTachoInstallerModel } from '../../core/domainModels/versions/AddTachoInstallerModel';
import { TachoReleaseInformationModel } from '../../core/domainModels/versions/TachoInstallerModel';
import { UpdateTachoInstallerModel } from '../../core/domainModels/versions/UpdateTachoInstallerModel';
import storage from 'redux-persist/es/storage';
import autoMergeLevel1 from 'redux-persist/es/stateReconciler/autoMergeLevel1';
import { PersistConfig } from 'redux-persist';
import { fieldName } from '../../core/helpers/aggrid';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';

export interface VersionsState {
    installers: Array<TachoReleaseInformationModel>;
    fetchAllInstallersPending: boolean;
    fetchAllInstallersError: string;
    addNewInstallerPending: boolean;
    addNewInstallerError: string;
    deleteInstallerPending: boolean;
    deleteInstallerError: string;
    updateInstallerPending: boolean;
    updateInstallerError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;
    uploadPercent: number;
    uploadLoaded: number;
    uploadTotal: number;
}

const initialState: VersionsState = {
    installers: [],
    fetchAllInstallersPending: false,
    fetchAllInstallersError: '',
    addNewInstallerPending: false,
    addNewInstallerError: '',
    deleteInstallerPending: false,
    deleteInstallerError: '',
    updateInstallerPending: false,
    updateInstallerError: '',
    displayPreferences: [],
    tableFilters: {},
    uploadPercent: 0,
    uploadLoaded: 0,
    uploadTotal: 0,
};

const name = 'versions';

const getAllInstallersThunk = createAsyncThunk(`${name}/fetchAll`, async (params, thunkAPI) => {
    try {
        return await getAllInstallers();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const addNewInstallerThunk = createAsyncThunk(`${name}/addNewInstaller`, async (params: AddTachoInstallerModel, thunkAPI) => {
    try {
        return await addNewInstaller(params, (percent, loaded, total) => {
            thunkAPI.dispatch(uploadFileAction({ percent, loaded, total }));
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const updateInstallerThunk = createAsyncThunk(`${name}/updateInstaller`, async (params: UpdateTachoInstallerModel, thunkAPI) => {
    try {
        return await updateInstaller(params, (percent, loaded, total) => {
            thunkAPI.dispatch(uploadFileAction({ percent, loaded, total }));
        });
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const deleteInstallerThunk = createAsyncThunk(`${name}/deleteInstaller`, async (installerIds: Array<string>, thunkAPI) => {
    try {
        return await removeInstallers(installerIds);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const uploadFileAction = createAction<{ percent: number; loaded: number; total: number }>('root/reset');

const versionsSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyVersionsDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyVersionsTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
        resetDeleteInstallerError(state) {
            state.deleteInstallerError = '';
        },
    },
    extraReducers: builder => {
        builder
            .addCase(getAllInstallersThunk.pending, (state, action) => {
                state.fetchAllInstallersPending = true;
                state.fetchAllInstallersError = '';
            })
            .addCase(getAllInstallersThunk.fulfilled, (state, action) => {
                state.fetchAllInstallersError = '';
                state.installers = action.payload;
                state.fetchAllInstallersPending = false;
            })
            .addCase(getAllInstallersThunk.rejected, (state, action) => {
                state.fetchAllInstallersPending = false;
                state.fetchAllInstallersError = action.payload as string;
            })
            .addCase(updateInstallerThunk.pending, (state, action) => {
                state.updateInstallerPending = true;
                state.updateInstallerError = '';
            })
            .addCase(updateInstallerThunk.fulfilled, (state, action) => {
                const updatedInstallerIndex = state.installers.findIndex(c => c.id === action.payload.id);
                if (updatedInstallerIndex !== -1) {
                    state.installers[updatedInstallerIndex] = { ...state.installers[updatedInstallerIndex], ...action.payload };
                }
                state.updateInstallerPending = false;
            })
            .addCase(updateInstallerThunk.rejected, (state, action) => {
                state.updateInstallerPending = false;
                state.updateInstallerError = action.payload as string;
            })
            .addCase(deleteInstallerThunk.pending, (state, action) => {
                state.deleteInstallerPending = true;
                state.deleteInstallerError = '';
            })
            .addCase(deleteInstallerThunk.fulfilled, (state, action) => {
                state.deleteInstallerError = '';
                state.installers = state.installers.filter(item => !action.payload.includes(item.id));
                state.deleteInstallerPending = false;
            })
            .addCase(deleteInstallerThunk.rejected, (state, action) => {
                state.deleteInstallerPending = false;
                state.deleteInstallerError = action.payload as string;
            })
            .addCase(addNewInstallerThunk.pending, (state, action) => {
                state.addNewInstallerPending = true;
                state.addNewInstallerError = '';
            })
            .addCase(addNewInstallerThunk.fulfilled, (state, action) => {
                state.addNewInstallerError = '';
                state.installers = [...state.installers, action.payload];
                state.addNewInstallerPending = false;
            })
            .addCase(addNewInstallerThunk.rejected, (state, action) => {
                state.addNewInstallerPending = false;
                state.addNewInstallerError = action.payload as string;
            })
            .addCase(uploadFileAction, (state, action) => {
                state.uploadPercent = action.payload.percent;
                state.uploadTotal = action.payload.total;
                state.uploadLoaded = action.payload.loaded;
            });
    },
});

export const versionsReducerConfig: PersistConfig<VersionsState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<VersionsState>('displayPreferences'), fieldName<VersionsState>('tableFilters')],
};
const { reducer, actions } = versionsSlice;

const { modifyVersionsDisplayPreferences, modifyVersionsTableFilters, resetDeleteInstallerError } = actions;
export { reducer as versionsReducer };
export {
    getAllInstallersThunk,
    addNewInstallerThunk,
    updateInstallerThunk,
    deleteInstallerThunk,
    modifyVersionsDisplayPreferences,
    modifyVersionsTableFilters,
    resetDeleteInstallerError,
};
